import { Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { MenuCategoriesService } from '../services/menu-categories.service';

import {
    SetLastSection,
    LoadLastSection,
    OpenCategoriesInHeader,
    NavigateToCategoryPageAnalytics,
    SetLastSectionSuccess,
    SetSeoHome,
    SetActiveCategories,
    SetActiveCategoryIdLvl1,
    OpenMenu,
    ChangeCurrentCategoryAction,
} from './menu-categories.actions';
import {
    getActiveCategories,
    getLastSectionSelector,
} from './menu-categories.selectors';
import { ActiveCategoriesTree } from './menu-categories.state';

import { ApplicationSections, ApplicationSectionsNumber } from '@core/domain/models/application-section.model';
import { Category } from '@core/domain/models/category.models';
import * as fromCoreEvents from '@core/infrastructure/state/core-events.actions';
import { PageTitleService } from '@core/services/page-title.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { CategoriesPublicCommands, CategoriesSelectors } from '@features/category';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { LoadCategorySuccess, getCategory } from '@pages/category/public_api';
import { LoadProductPageSuccess } from '@pages/product';
import { MenuHeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { MenuCategory } from '@shared/header/models/category.model';
import { HeaderUtilsService } from '@shared/header/services/header-utils.service';

@Injectable()
export class MenuCategoriesEffects {
    private readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    private readonly getMenuCategories$:
    Observable<MenuCategory[]> = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getLastSectionSelector$: Observable<string> = this.store.select(getLastSectionSelector);

    private readonly getActiveCategories$: Observable<
    Record<
    number, ActiveCategoriesTree
    >> = this.store.select(getActiveCategories);

    private readonly getDataFromRouter$: Observable<Data> = this.store.select(routerPublicQueries.getDataFromRouter);

    private readonly getParamsFromRouter$ = this.store.select(routerPublicQueries.getParamsFromRouter);

    private readonly getMergedRoute$ = this.store.select(routerPublicQueries.getMergedRoute);

    constructor(
        private readonly actions$: Actions,
        private readonly headerService: HeaderUtilsService,
        private readonly menuCategoriesService: MenuCategoriesService,
        private readonly pageTitleService: PageTitleService,
        private readonly storageService: CoreStorageService,
        private readonly store: Store,
        private readonly platform: PlatformService
    ) { }

    public LoadMenuCategoriesSuccessEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CategoriesPublicCommands.LoadMenuCategoriesSuccess),
            map(() => LoadLastSection())
        );
    });

    public setLastSectionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLastSection),
            distinctUntilChanged((a, b) => a.section === b.section),
            map(({ section, navigation }) => SetLastSectionSuccess({ section, navigation }))
        );
    });

    public changeCurrentCategorySuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            switchMap(() => this.store.select(CategoriesSelectors.getMenuCategories).pipe(
                filter((menuCategories) => menuCategories?.length > 0),
            )),
            switchMap(() => this.menuCategoriesService.getCurrentCategoryListeningUrl$),
            filter(({ category }) => !!category),
            map(({ category, categories }) => ChangeCurrentCategoryAction({ category, categories }))
        );
    });

    public setCategoryActiveOnCategorySuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCurrentCategoryAction),
            map(({ category, categories }) => {
                let index = categories.findIndex(cat => HeaderUtilsService.isChildOfCategory(cat, category.id));
                if (index < 0) { index = 0; }
                return SetLastSection({ section: HeaderUtilsService.getSectionByIndex(index), navigation: false });
            })
        );
    });

    public setParrillasActiveCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCurrentCategoryAction),
            map(({ categories, category }) => {
                const categoryLevels = HeaderUtilsService.getCategoriesLevelsById(category?.id, categories);
                return SetActiveCategories({ data: categoryLevels });
            })
        );
    });

    public setLastSectionToStorage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLastSection),
            filter(() => this.platform.isBrowser()),
            tap((action) => {
                this.storageService.setValue('LAST_SECTION', action.section, 'PERMANENT', 'FUNCTIONAL_PERSONALIZATION');
            })
        );
    }, { dispatch: false });

    public setSeoHomePageEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCoreEvents.ConfigureHomeLinkSuccess, SetSeoHome),
            concatLatestFrom(() => this.getMergedRoute$),
            tap(([_, data]) => {
                if (data?.url?.includes('/cn/en')) { // China/English case
                    this.pageTitleService.setTitle('麦西姆杜特 (Massimo Dutti)');
                }
            })
        );
    }, { dispatch: false });

    public showCategoryInMenuSelectorEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                OpenCategoriesInHeader
            ),
            map((action: any) => action.categoryId),
            concatLatestFrom(() => this.getMenuCategories$),
            tap(([categoryId, categories]) => {
                const index = categories.findIndex(cat => HeaderUtilsService.isChildOfCategory(cat, categoryId));
                this.headerService.openMenuByCategoryId(categoryId, index);
            }),
        );
    }, { dispatch: false });

    public navigateToCategoryPageAnalyticsEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                NavigateToCategoryPageAnalytics
            ),
            map((action: any) => action.category),
            concatLatestFrom(() => this.getLastSectionSelector$),
            map(([category, lastSection]: [MenuCategory, string]) => {
                return MenuHeaderEventAnalytics({
                    section: lastSection.toLowerCase(),
                    categoryPath: category.sectionPathEn,
                });
            })
        );
    });

    public loadHomeSuccessEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductPageSuccess),
            concatLatestFrom(() => this.getLastSectionSelector$),
            filter(([_action, lastSection]) => lastSection !== ApplicationSections.WOMEN),
            map(([{ product }, lastSection]) => {
                const section = lastSection || ApplicationSectionsNumber[product?.section] || ApplicationSections.WOMEN;
                return SetLastSection({ section, navigation: false });
            }),
        );
    });

    public setActiveCategoryIdLvl1$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetActiveCategoryIdLvl1),
            withLatestFrom(this.getActiveCategories$),
            filter(([_, activeCategories]) => !!activeCategories),
            map(([action, activeCategories]) => {
                const newActiveCategories = Object.values(activeCategories).map((catActive, index) => {
                    if (index === action.index) {
                        return {
                            ...catActive,
                            activeCategoryIdLvl1: action.activeCategoryIdLvl1,
                        };
                    }
                    return catActive;
                });
                return SetActiveCategories({ data: newActiveCategories });
            })
        ));

    public resetMenuActiveCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadLastSection),
            switchMap(() => this.getDataFromRouter$.pipe(filter(data => !!data))),
            filter(params => !!params && !this.headerService.isCategoryPage(params.pageName)),
            withLatestFrom(this.getMenuCategories$),
            map(([_, menuCategories]) => {
                const newActiveCategories = HeaderUtilsService.getCategoriesLevelsById(undefined, menuCategories);
                return SetActiveCategories({ data: newActiveCategories });
            })
        );
    });

    public resetMenuOnOpenMenu$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenMenu),
            concatLatestFrom(() => [
                this.getMenuCategories$,
                this.getCategory$,
                this.getDataFromRouter$.pipe(filter(data => !!data)),
                this.getParamsFromRouter$.pipe(filter(data => !!data)),
            ]),
            map(([action, menuCategories, category, params]) => {
                return {
                    action: action,
                    menuCategories,
                    category,
                    pageName: params.pageName,
                };
            }),
            filter(({ pageName, category }) => !this.headerService.isCategoryPage(pageName) && !!category?.id),
            map(({ menuCategories, category }) => {
                const newActiveCategories = HeaderUtilsService.getCategoriesLevelsById(category.id, menuCategories);
                return SetActiveCategories({ data: newActiveCategories });
            })
        );
    });
}
